import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import { getApprover } from '../../../../utils/services/user.service';

export default function AddActivityModal({
  show,
  onHide,
  draftType,
  setDraftType,
  draftTitle,
  setDraftTitle,
  draftDate,
  setDraftDate,
  draftTime,
  setDraftTime,
  draftNote,
  setDraftNote,
  draftPriority,
  setDraftPriority,
  draftInvitedUserName,
  setDraftInvitedUserName,
  draftRemind,
  setDraftRemind,
  createActivity,
  createLoading,
}) {
  const [inviteSearchInput, setInviteSearchInput] = useState('');
  const [inviteOptions, setInviteOptions] = useState([]);
  const [inviteSearchLoading, setInviteSearchLoading] = useState(false);
  const [inviteDropOpen, setInviteDropOpen] = useState(false);
  const [inviteDropHi, setInviteDropHi] = useState(0);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (!show) {
      setInviteSearchInput('');
      setInviteOptions([]);
      setInviteDropOpen(false);
    } else if (draftInvitedUserName) {
      setInviteSearchInput(draftInvitedUserName);
    }
  }, [show, draftInvitedUserName]);

  const handleInviteSearch = (value) => {
    setInviteSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value || value.length < 2) {
      setInviteOptions([]);
      setInviteDropOpen(false);
      setDraftInvitedUserName('');
      return;
    }
    searchTimeoutRef.current = setTimeout(async () => {
      setInviteSearchLoading(true);
      try {
        const res = await getApprover({ name: value });
        const users = res?.data?.user || [];
        const search = value.toLowerCase();
        const filtered = users.filter((u) => {
          const name = (u.name || '').toLowerCase();
          const nameWords = name.split(/\s+/);
          return nameWords.some((word) => word.startsWith(search));
        });
        const opts = filtered.map((i) => ({ value: i.name, label: i.name }));
        setInviteOptions(opts);
        setInviteDropOpen(opts.length > 0);
        setInviteDropHi(0);
      } catch {
        setInviteOptions([]);
        setInviteDropOpen(false);
      } finally {
        setInviteSearchLoading(false);
      }
    }, 300);
  };

  const handleInviteSelect = (opt) => {
    setDraftInvitedUserName(opt.value);
    setInviteSearchInput(opt.value);
    setInviteDropOpen(false);
    setInviteOptions([]);
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="default-pages-calendar">
      <Modal.Header closeButton>
        <Modal.Title>Add Activity</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="form">
          <div className="formRow formRowInline">
            <div className="formField">
              <label className="lab">Type</label>
              <select className="sel" value={draftType} onChange={(e) => setDraftType(e.target.value)}>
                <option value="UserTask">Task</option>
                <option value="Login">Access</option>
                <option value="Order">Order</option>
                <option value="Receive">Receive</option>
                <option value="Send">Send</option>
                <option value="PasswordChange">Password</option>
                <option value="Security">Security</option>
                <option value="System">System</option>
              </select>
            </div>
            <div className="formField formFieldFlex">
              <label className="lab">Title</label>
              <input className="inp" value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} placeholder="e.g., Call supplier, Upload invoice" />
            </div>
          </div>
          <div className="form2">
            <div className="formRow">
              <label className="lab">Date</label>
              <input className="inp" type="date" value={draftDate} onChange={(e) => setDraftDate(e.target.value)} />
            </div>
            <div className="formRow">
              <label className="lab">Time</label>
              <input className="inp" type="time" value={draftTime} onChange={(e) => setDraftTime(e.target.value)} />
            </div>
          </div>
          <div className="formRow">
            <label className="lab">Priority</label>
            <select className="sel" value={draftPriority} onChange={(e) => setDraftPriority(e.target.value)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div className="formRow">
            <label className="lab">Note (optional)</label>
            <textarea className="ta" value={draftNote} onChange={(e) => setDraftNote(e.target.value)} placeholder="Optional notes…" />
          </div>
          {draftType === 'UserTask' && (
            <div className="form2">
              <div className="formRow">
                <label className="lab">Invite (user)</label>
                <div className="mentionWrap">
                  <input
                    className="inp"
                    value={inviteSearchInput}
                    onChange={(e) => handleInviteSearch(e.target.value)}
                    onFocus={() => inviteOptions.length > 0 && setInviteDropOpen(true)}
                    onBlur={() => setTimeout(() => setInviteDropOpen(false), 150)}
                    onKeyDown={(e) => {
                      if (!inviteDropOpen || !inviteOptions.length) return;
                      if (e.key === 'ArrowDown') { e.preventDefault(); setInviteDropHi((i) => Math.min(i + 1, inviteOptions.length - 1)); }
                      else if (e.key === 'ArrowUp') { e.preventDefault(); setInviteDropHi((i) => Math.max(i - 1, 0)); }
                      else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); handleInviteSelect(inviteOptions[inviteDropHi] || inviteOptions[0]); }
                      else if (e.key === 'Escape') setInviteDropOpen(false);
                    }}
                    placeholder="Type 2+ chars to search user..."
                  />
                  {inviteSearchLoading && <span className="muted miniNote">Searching…</span>}
                  {inviteDropOpen && inviteOptions.length > 0 && (
                    <div className="mentionDrop" role="listbox" aria-label="User suggestions">
                      {inviteOptions.map((u, idx) => (
                        <button
                          key={u.value}
                          className={`mentionItem ${idx === inviteDropHi ? 'mentionItemOn' : ''}`}
                          onMouseDown={(ev) => { ev.preventDefault(); handleInviteSelect(u); }}
                          role="option"
                          aria-selected={idx === inviteDropHi}
                          type="button"
                        >
                          <span className="mentionH">{u.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="help">Tip: type to search and select a user to invite.</div>
              </div>
              <div className="formRow">
                <label className="lab">Reminder</label>
                <div className="remRow">
                  <select className="sel" value={draftRemind} onChange={(e) => setDraftRemind(parseInt(e.target.value, 10))}>
                    <option value={0}>Off</option>
                    <option value={5}>5 min before</option>
                    <option value={15}>15 min before</option>
                    <option value={30}>30 min before</option>
                    <option value={60}>60 min before</option>
                    <option value={180}>3 hours before</option>
                  </select>
                  <span className="muted miniNote">Minutes before activity</span>
                </div>
              </div>
            </div>
          )}
          <div className="formActions">
            <button className="ghost" onClick={onHide} disabled={createLoading}>Cancel</button>
            <button className="primary" onClick={createActivity} disabled={createLoading}>
              {createLoading ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
